"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { SuperLink as Link } from "@/components/common/super-link";
import { createPath } from "@/service/routing/routing";
import { scrollToPos } from "@/service/scroll/scroll";

export const Icd10CategoryList = ({
  categories,
  categorySlug,
}: {
  categories: Icd10RootVM[];
  categorySlug?: string;
}) => {
  return (
    <div className={""}>
      {categories.map((root: Icd10RootVM, index: number) => (
        <Category
          key={index}
          index={index}
          code={root.code}
          slug={root.slug}
          name={root.name}
          categorySlug={categorySlug}
          children={root.children}
        />
      ))}
    </div>
  );
};

const Category = ({
  code = "",
  slug = "",
  name = "",
  categorySlug = "",
  children,
}: {
  index: number;
  code: string;
  slug: string;
  name: string;
  categorySlug?: string;
  children: Icd10PreviewVM[] | null;
}) => {
  const [isActive, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (categorySlug === slug) {
      setActive(true);
      scrollToPos({
        top: ref?.current?.getBoundingClientRect()?.top - 64,
      });
    }
  }, []);
  return (
    <div className={"w-full"} ref={ref}>
      <div
        className={
          "hover:text-remedium-blue flex w-full justify-between border-b-[1px] border-zinc-200 py-3 transition-all hover:cursor-pointer hover:[&_.to-scale]:scale-105 active:[&_.to-scale]:scale-95"
        }
        onClick={() => {
          setActive(!isActive);
        }}
      >
        <div className={"flex items-center gap-4 select-none"}>
          <div
            className={`to-scale flex h-[28px] max-w-[28px] min-w-[28px] items-center justify-center rounded-md bg-indigo-900 text-sm font-medium text-white transition-all`}
          >
            {code}
          </div>
          <div className={"text-sm md:text-base"}>{name}</div>
        </div>
        <div className={"flex items-center"}>
          <ChevronDownIcon
            size={"20px"}
            className={cn("transition-all", isActive && "rotate-180")}
          />
        </div>
      </div>
      <div className={cn("mx-11 my-4 hidden", isActive && "block")}>
        {children && <ItemList items={children} />}
      </div>
    </div>
  );
};

const ItemList = ({ items }: { items: Icd10PreviewVM[] }) => {
  return (
    <div className="gap flex flex-col gap-5 select-none">
      {items.map(({ parent, slug, code, name }: Icd10PreviewVM) => (
        <Link
          key={name}
          className={"flex items-start gap-1.5"}
          href={createPath("icd10.slug", {
            categorySlug: parent?.slug,
            codeSlug: slug,
          })}
        >
          <div
            className={cn(
              `text-remedium-blue max-w-[30px] min-w-[30px] text-sm font-bold`,
            )}
          >
            {code}
          </div>
          <div
            className={`text-foreground hover:text-remedium-blue text-sm font-medium transition-all`}
          >
            {name}
          </div>
        </Link>
      ))}
    </div>
  );
};
